import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Sizes } from '../utils/sizes';
import { Colors } from '../utils/colors';
import { authorizeDownload } from '../redux/slices/ebookDownloadSlice';

const CardContainer = styled.div`
  background: #111;
  border: 1px solid rgba(255,215,0,0.1);
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;

  &:hover {
    border-color: rgba(255,215,0,0.4);
    box-shadow: 0 12px 40px rgba(0,0,0,0.5);
    transform: translateY(-4px);

    .prod-image { transform: scale(1.04); }
    .prod-btn { opacity: 1; transform: translateY(0); }
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  aspect-ratio: 3 / 4;
  overflow: hidden;
  position: relative;
  background: #1a1a1a;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top center;
    display: block;
    transition: transform 0.3s;
  }
`;

const BadgeFree = styled.span`
  position: absolute;
  top: 10px;
  left: 10px;
  background: #22c55e;
  color: #fff;
  font-size: 10px;
  font-weight: 800;
  padding: 3px 8px;
  border-radius: 4px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  z-index: 1;
`;

const DiscountBadge = styled.span`
  position: absolute;
  top: 10px;
  right: 10px;
  background: ${Colors.yellow};
  color: #0a0a0a;
  font-size: 10px;
  font-weight: 900;
  padding: 3px 8px;
  border-radius: 4px;
  z-index: 1;
`;

const Content = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 8px;
`;

const Category = styled.span`
  color: ${Colors.yellow};
  font-size: 10px;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 1.2px;
  opacity: 0.8;
`;

const Title = styled.h3`
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  margin: 0;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Description = styled.p`
  color: rgba(255,255,255,0.45);
  font-size: 12px;
  line-height: 1.5;
  margin: 0;
  flex-grow: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;

  .price {
    color: ${Colors.yellow};
    font-size: 17px;
    font-weight: 900;
  }

  .original {
    color: rgba(255,255,255,0.3);
    font-size: 12px;
    text-decoration: line-through;
  }

  .free {
    color: #22c55e;
    font-size: 13px;
    font-weight: 800;
  }
`;

const QuickButton = styled.button`
  width: 100%;
  padding: 10px;
  margin-top: 8px;
  background: ${Colors.yellow};
  color: #0a0a0a;
  border: none;
  border-radius: 6px;
  font-weight: 800;
  font-size: 12px;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: opacity 0.2s, transform 0.2s;
  opacity: 0;
  transform: translateY(6px);

  @media (max-width: ${Sizes.mediaQueryBreak3}) {
    opacity: 1;
    transform: none;
  }

  &:hover { background: #ffe033; }
`;

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);

  const isFree = product.isFree || product.price === 0;
  const discount = product.originalPrice && product.price
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  const handleCardClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/produto/${product.id}`);
  };

  const handleQuickView = (e) => {
    e.stopPropagation();
    if (isFree) {
      if (!token) { navigate('/login', { state: { from: '/' } }); return; }
      dispatch(authorizeDownload({ ebookId: product.id, ebookTitle: product.title, paymentInfo: { method: 'free' } }));
      window.location.href = product.downloadUrl;
    } else {
      handleCardClick();
    }
  };

  return (
    <CardContainer onClick={handleCardClick} role="button" tabIndex="0"
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleCardClick(); }}
    >
      <ImageWrapper>
        {isFree && <BadgeFree>Grátis</BadgeFree>}
        {!isFree && discount > 0 && <DiscountBadge>-{discount}%</DiscountBadge>}
        <img
          className="prod-image"
          src={product.image}
          alt={product.title}
          loading="lazy"
        />
      </ImageWrapper>

      <Content>
        <Category>{product.category}</Category>
        <Title>{product.title}</Title>
        <Description>{product.description}</Description>

        <PriceRow>
          {isFree ? (
            <span className="free">✓ Gratuito</span>
          ) : (
            <>
              <span className="price">R$ {(product.price / 100).toFixed(2).replace('.', ',')}</span>
              {product.originalPrice > product.price && (
                <span className="original">R$ {(product.originalPrice / 100).toFixed(2).replace('.', ',')}</span>
              )}
            </>
          )}
        </PriceRow>

        <QuickButton className="prod-btn" onClick={handleQuickView}>
          {isFree ? '⬇ Baixar grátis' : 'Ver detalhes →'}
        </QuickButton>
      </Content>
    </CardContainer>
  );
};

export default ProductCard;
